import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import ProviderService from '../../services/providerService';
import './Providers.css';

const Providers = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadProviders = useCallback(async () => {
    try {
      setInitialLoading(true);
      const token = auth.user?.access_token;
      const response = await ProviderService.getAllProviders(token);
      setProviders(response.data || []);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al cargar los proveedores'
      });
    } finally {
      setInitialLoading(false);
    }
  }, [auth.user?.access_token]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  useEffect(() => {
    if (!searchFilter.trim()) {
      setFilteredProviders(providers);
      return;
    }

    const term = searchFilter.toLowerCase();
    const filtered = providers.filter(provider => {
      const nameMatch = provider.name?.toLowerCase().includes(term);
      const contactMatch = provider.contactName?.toLowerCase().includes(term);
      const addressMatch = provider.address?.toLowerCase().includes(term);
      const servicesMatch = (provider.services || []).some(service => service.toLowerCase().includes(term));
      return nameMatch || contactMatch || addressMatch || servicesMatch;
    });

    setFilteredProviders(filtered);
  }, [searchFilter, providers]);

  const handleSearchChange = (e) => {
    setSearchFilter(e.target.value);
  };

  const clearSearch = () => {
    setSearchFilter('');
  };

  const handleAddProvider = () => {
    navigate('/laboratory-orders/providers/new');
  };

  const handleViewProvider = (providerId) => {
    navigate(`/laboratory-orders/providers/${providerId}`);
  };

  const handleEditProvider = (providerId) => {
    navigate(`/laboratory-orders/providers/${providerId}/edit`);
  };

  if (initialLoading) {
    return (
      <div className="providers-container">
        <div className="providers-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="providers-container">
      <div className="providers-header">
        <div className="header-content">
          <h1>Proveedores y Laboratorios</h1>
          <p>Centraliza los contactos y servicios de laboratorios externos</p>
        </div>
        <button className="btn-primary add-btn" onClick={handleAddProvider}>
          Agregar Proveedor
        </button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="providers-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Buscar por nombre, contacto o servicio..."
            value={searchFilter}
            onChange={handleSearchChange}
            className="search-input"
          />
          {searchFilter && (
            <button
              className="clear-search-btn"
              onClick={clearSearch}
              title="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="providers-table-container">
        {filteredProviders.length === 0 ? (
          <div className="no-providers">
            <p>No se encontraron proveedores</p>
            {searchFilter && (
              <button className="btn-secondary" onClick={clearSearch}>
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            <table className="providers-table">
              <thead>
                <tr>
                  <th>Proveedor</th>
                  <th>Contacto</th>
                  <th>Entrega Promedio</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredProviders.map(provider => (
                  <tr key={provider.id}>
                    <td className="provider-name">
                      <div className="provider-details">
                        <span className="name">{provider.name}</span>
                        {provider.address && (
                          <span className="muted">{provider.address}</span>
                        )}
                      </div>
                    </td>
                    <td className="provider-contact">
                      <div className="stack">
                        {provider.contactName && <span>{provider.contactName}</span>}
                        {provider.phone && <span className="muted">{provider.phone}</span>}
                        {provider.email && <span className="muted">{provider.email}</span>}
                      </div>
                    </td>
                    <td className="provider-delivery">
                      {provider.deliveryTimeDays !== null && provider.deliveryTimeDays !== undefined
                        ? `${provider.deliveryTimeDays} dias`
                        : '-'}
                    </td>
                    <td>
                      <div className="provider-actions">
                        <button
                          className="provider-action-btn"
                          onClick={() => handleViewProvider(provider.id)}
                          title="Ver proveedor"
                        >
                          Ver
                        </button>
                        <button
                          className="provider-action-btn"
                          onClick={() => handleEditProvider(provider.id)}
                          title="Editar proveedor"
                        >
                          Editar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="providers-mobile-cards">
              {filteredProviders.map(provider => (
                <div key={provider.id} className="provider-card">
                  <div className="provider-card-header">
                    <div className="provider-card-title">{provider.name}</div>
                  </div>
                  <div className="provider-card-content">
                    {provider.contactName && (
                      <div className="card-detail">
                        <strong>Contacto:</strong> {provider.contactName}
                      </div>
                    )}
                    {provider.phone && (
                      <div className="card-detail">
                        <strong>Telefono:</strong> {provider.phone}
                      </div>
                    )}
                    {provider.email && (
                      <div className="card-detail">
                        <strong>Email:</strong> {provider.email}
                      </div>
                    )}
                  </div>
                  <div className="provider-card-actions">
                    <button
                      className="provider-action-btn"
                      onClick={() => handleViewProvider(provider.id)}
                      title="Ver proveedor"
                    >
                      Ver
                    </button>
                    <button
                      className="provider-action-btn"
                      onClick={() => handleEditProvider(provider.id)}
                      title="Editar proveedor"
                    >
                      Editar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Providers;
